import { Component, OnInit, TemplateRef, ViewChild, ChangeDetectorRef, inject } from '@angular/core';
import { BsDropdownConfig } from 'ngx-bootstrap/dropdown';
import {
  ApiService,
  AuthService,
  BlockAbstractComponent,
  CacheService,
  PageService,
  ParserService,
} from '@gallaghercp/core-frontend';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { BreakpointObserver } from '@angular/cdk/layout';

@Component({
  selector: 'core-client-block-language',
  templateUrl: './block-language.component.html',
  styleUrl: './block-language.component.scss',
  standalone: false,
  providers: [
    {
      provide: BsDropdownConfig,
      useValue: { isAnimated: false, autoClose: true },
    },
  ],
})
export class BlockClientLanguageComponent extends BlockAbstractComponent implements OnInit {
  protected override parser: ParserService;
  protected override auth: AuthService;
  protected override pages: PageService;
  protected modalService = inject(BsModalService);
  protected cache = inject(CacheService);
  protected api = inject(ApiService);
  protected breakpoint = inject(BreakpointObserver);
  private cdr = inject(ChangeDetectorRef);

  @ViewChild('languageSelect', { static: true })
  languageModal!: TemplateRef<any>;

  languages$: any;
  zone!: string;
  modalRef?: BsModalRef;
  isLargeBreakpoint!: boolean;
  private languagesLoaded = false;
  private pendingZoneChange: number | false | null = null;

  constructor() {
    const parser = inject(ParserService);
    const auth = inject(AuthService);
    const pages = inject(PageService);

    super(parser, auth, pages);

    this.parser = parser;
    this.auth = auth;
    this.pages = pages;
  }

  override ngOnInit(): void {
    // console.log('🌐 BlockClientLanguageComponent - ngOnInit called');
    // console.log(
    //     '🌐 BlockClientLanguageComponent - Current language on init:',
    //     this.getCurrentLanguage(),
    // );

    super.ngOnInit();

    // Load languages first
    this.subscriptions.push(
      this.translateService.getLanguages().subscribe((languages: any) => {
        // console.log(
        //     '🌐 BlockClientLanguageComponent - Languages loaded:',
        //     languages,
        // );
        // console.log(
        //     '🌐 BlockClientLanguageComponent - Languages count:',
        //     languages?.length,
        // );

        // Reorder languages to put English first
        this.languages$ = this.reorderLanguages(languages);
        this.languagesLoaded = true;

        // Get current zone and language
        this.zone = this.api.getServiceZone();
        const currentLang = this.getCurrentLanguage();

        // console.log(
        //     '🌐 BlockClientLanguageComponent - Available languages:',
        //     this.languages$.map((l: any) => l.code),
        // );
        // console.log(
        //     '🌐 BlockClientLanguageComponent - Is multi-language?',
        //     this.isMultiLanguage(),
        // );
        // console.log(
        //     '🌐 BlockClientLanguageComponent - Current zone:',
        //     this.zone,
        // );
        // console.log(
        //     '🌐 BlockClientLanguageComponent - Current language:',
        //     currentLang,
        // );

        // Check if the current language is available in this zone
        const isCurrentLanguageAvailable = this.isLanguageAvailable(currentLang);
        // console.log(
        //     '🌐 BlockClientLanguageComponent - Is current language available in this zone?',
        //     isCurrentLanguageAvailable,
        // );

        if (isCurrentLanguageAvailable) {
          // Current language is available, use it
          this.translateProvider.use(currentLang);
          // console.log(
          //     '🌐 BlockClientLanguageComponent - Using current language:',
          //     currentLang,
          // );
        } else {
          // Current language not available, fallback to first available language
          const fallbackLanguage = this.languages$[0]?.code || 'en_GB';
          // console.log(
          //     '🌐 BlockClientLanguageComponent - Current language not available, falling back to:',
          //     fallbackLanguage,
          // );
          this.setLanguage({ code: fallbackLanguage });
        }

        // Trigger change detection to update template
        this.cdr.detectChanges();

        // Process any pending zone change
        if (this.pendingZoneChange !== null) {
          // console.log(
          //     '🌐 BlockClientLanguageComponent - Processing pending zone change:',
          //     this.pendingZoneChange,
          // );
          this.handleZoneChange(this.pendingZoneChange);
          this.pendingZoneChange = null;
        }
      }),
    );

    // Handle zone changes - but wait for languages to load
    this.subscriptions.push(
      this.api.zoneChangeEmit.subscribe((zone: number | false) => {
        // console.log(
        //     '🌐 BlockClientLanguageComponent - Zone change detected:',
        //     zone,
        // );
        // console.log(
        //     '🌐 BlockClientLanguageComponent - Languages loaded?',
        //     this.languagesLoaded,
        // );

        if (this.languagesLoaded) {
          this.handleZoneChange(zone);
        } else {
          // console.log(
          //     '🌐 BlockClientLanguageComponent - Languages not loaded yet, storing pending zone change',
          // );
          this.pendingZoneChange = zone;
        }
      }),
    );

    this.breakpoint.observe(['(min-width: 993px)']).subscribe(result => {
      this.isLargeBreakpoint = result.matches;
    });
  }

  private handleZoneChange(zone: number | false): void {
    // private handleZoneChange(): void {
    // console.log(
    //     '🌐 BlockClientLanguageComponent - Handling zone change:',
    //     zone,
    // );

    // const oldZone = this.zone;
    const currentLang = this.getCurrentLanguage();

    // console.log(
    //     '🌐 BlockClientLanguageComponent - Zone changing from',
    //     oldZone,
    //     'to',
    //     zone,
    // );
    // console.log(
    //     '🌐 BlockClientLanguageComponent - Current language before zone change:',
    //     currentLang,
    // );

    // Update the zone
    this.zone = this.api.getServiceZone();

    // Re-fetch languages for the new zone
    // console.log(
    //     '🌐 BlockClientLanguageComponent - Fetching languages for new zone...',
    // );
    this.translateService.getLanguages().subscribe((newLanguages: any) => {
      // console.log(
      //     '🌐 BlockClientLanguageComponent - New zone languages:',
      //     newLanguages,
      // );
      // console.log(
      //     '🌐 BlockClientLanguageComponent - New zone language codes:',
      //     newLanguages?.map((l: any) => l.code),
      // );

      this.languages$ = this.reorderLanguages(newLanguages);

      // Check if the current language is available in the new zone
      const isCurrentLanguageAvailable = this.isLanguageAvailable(currentLang);
      // console.log(
      //     '🌐 BlockClientLanguageComponent - Is current language available in new zone?',
      //     isCurrentLanguageAvailable,
      // );

      if (isCurrentLanguageAvailable) {
        // Current language is available in new zone, keep using it
        // console.log(
        //     '🌐 BlockClientLanguageComponent - Keeping current language:',
        //     currentLang,
        // );
        this.translateProvider.use(currentLang);
      } else {
        // Current language not available in new zone, fallback to first available
        const fallbackLanguage = this.languages$[0]?.code || 'en_GB';
        // console.log(
        //     '🌐 BlockClientLanguageComponent - Current language not available in new zone, falling back to:',
        //     fallbackLanguage,
        // );
        this.setLanguage({ code: fallbackLanguage });
      }

      // Trigger change detection to update template visibility
      this.cdr.detectChanges();

      // console.log(
      //     '🌐 BlockClientLanguageComponent - Zone change processing complete',
      // );
    });
  }

  private isLanguageAvailable(languageCode: string): boolean {
    if (!this.languages$ || !languageCode) return false;
    return this.languages$.some((lang: any) => lang.code === languageCode);
  }

  getCurrentLanguage(): string {
    return this.translateService.getCurrentLanguage() as string;
  }

  isMultiLanguage(): boolean {
    return this.languages$ && this.languages$.length > 1;
  }

  getLanguageName(code: string): string {
    if (!this.languages$) return code;

    const languageName = (this.languages$.filter((language: any) => language.code === code)[0] ?? {}).name;
    // Replace any English variant with just "English"
    if (languageName?.startsWith('English')) {
      return 'English';
    }

    return languageName || code;
  }

  setLanguage(language: { code: string }): void {
    // console.log(
    //     '🌐 BlockClientLanguageComponent - Setting language to:',
    //     language.code,
    // );
    this.translateService.setCurrentLanguage(language.code);
    this.translateProvider.use(language.code);
    this.closeModal();
  }

  openModal(template: TemplateRef<any>): void {
    if (!this.modalRef) {
      this.modalRef = this.modalService.show(template);
    }
  }

  closeModal(): void {
    if (this.modalRef) {
      this.modalRef.hide();
    }
  }

  getLanguageItemClasses(languageCode: string): string {
    const languageCodeClass: string = ['language-item--', languageCode].join('');
    return languageCode === this.getCurrentLanguage() ? 'active ' + languageCodeClass : languageCodeClass;
  }

  /**
   * Reorders the languages array to put English first, followed by other languages
   * @param languages The original languages array
   * @returns The reordered languages array
   */
  private reorderLanguages(languages: any[]): any[] {
    if (!languages || languages.length <= 1) {
      return languages;
    }

    // Find English language entry (assuming code starts with 'en')
    const englishLanguages = languages.filter(lang => lang.code.startsWith('en'));
    const otherLanguages = languages.filter(lang => !lang.code.startsWith('en'));

    // Return English first, then other languages
    return [...englishLanguages, ...otherLanguages];
  }
}
